import React, { useCallback, useMemo, useEffect } from 'react';
import { Button } from '@dsg-tech/homefield-react';
import { breakpointSelector } from 'store/selectors/commonSelector';
import { useAppSelector } from 'store/store';
import { getIsValidHost } from 'utility/common-utils';
import axios from 'axios';
import { getUserToken } from 'utility/auth-utils';
import { cartEditProductEvent } from '../../analytics/analytics-events';

type SelectedSkuType = {
  partNumber: string;
};

type CallbackArgumentType = {
  state: 'SUCCESS' | 'FAILURE';
  sku?: string;
};

type UpdateProductPropsType = {
  onClose: () => void;
  ecode: string;
  selectedSku: SelectedSkuType;
  skuDefaultValue: string;
  callback: (param: CallbackArgumentType) => void;
  isProductOOS: boolean;
};

const UpdateProduct = ({
  onClose,
  ecode,
  selectedSku,
  skuDefaultValue,
  callback,
  isProductOOS,
}: UpdateProductPropsType) => {
  const viewPortBreakpoint = useAppSelector(breakpointSelector);

  const host = window.location ? window.location.host : '';
  const getFavoritesUrl = () => {
    let returnUrl;

    if (getIsValidHost() || host.includes('prod-')) {
      returnUrl = `https://${window.location.host}/api/v1/carts`;
    } else {
      returnUrl = `/cart-api/api/v1/carts`;
    }

    return returnUrl;
  };

  const parsedSku = useMemo(() => {
    if (!selectedSku) return null;

    return selectedSku.partNumber;
  }, [selectedSku]);

  const updateProduct = useCallback(async () => {
    const token = await getUserToken();
    let hasToken = false;
    if (typeof token === 'string') {
      hasToken = true;
    }

    const response = await axios(`${getFavoritesUrl()}/contents/${skuDefaultValue}/update`, {
      method: 'PUT',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Cookie: host.includes('prod-') || host.includes('localhost') ? 'np0=true' : '',
        ...(hasToken ? { Authorization: token } : {}),
      },
      data: {
        sku: parsedSku,
      },
    });

    if (!callback) {
      onClose();
      return;
    }

    if (response.status === 200) {
      callback({ state: 'SUCCESS', sku: parsedSku });
      cartEditProductEvent({ EditedProductID: ecode, EditedProductSKU: skuDefaultValue });
      onClose();
      return;
    }

    callback({ state: 'FAILURE' });
  }, [parsedSku, skuDefaultValue]);

  const toChange = useMemo(() => {
    return parsedSku === skuDefaultValue || !parsedSku;
  }, [parsedSku, skuDefaultValue]);

  return (
    <div
      className={`quickview-button-edit-product-container ${
        viewPortBreakpoint === 'mobile' ? 'edit-mobile' : ''
      }`}
      data-testid="quickViewModal-update-buttons">
      <Button variant="secondary" label="Cancel" text="Cancel" onClick={onClose} />
      <Button
        variant="primary"
        label="Update item"
        text="Update item"
        onClick={updateProduct}
        disabled={toChange || isProductOOS}
      />
    </div>
  );
};

export default UpdateProduct;
